/**
 * VENTO LABS · K-ALMA Landing
 * 1) Cabecera minimalista: fondo negro difuminado al scrollear
 * 2) Vídeos ambiente: se reproducen al entrar en su sección y se pausan al salir
 * 3) Animación de aparición al hacer scroll (progressive enhancement)
 * Si JS no carga, todo el contenido queda visible en disposición estática.
 */
(() => {
  // El tema Horizon scrollea dentro de .page-wrapper, no en el body
  const scroller = document.querySelector('.page-wrapper');
  const scrollTarget = scroller || window;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- 1 · Cabecera con blur al scrollear --- */
  const header = document.querySelector('.vento-header');
  if (header) {
    const getTop = () => (scroller ? scroller.scrollTop : window.scrollY);
    const onScroll = () => {
      header.classList.toggle('vento-header--scrolled', getTop() > 24);
    };
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* --- 2 · Vídeos ambiente activados al entrar en el viewport --- */
  if ('IntersectionObserver' in window) {
    const videos = document.querySelectorAll('[data-vento-capsula-video] video, [data-vento-video-auto] video');
    if (videos.length) {
      const io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const video = entry.target;
            if (entry.isIntersecting && !reduceMotion) {
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          }
        },
        // Arranca antes de que el vídeo esté centrado, para no dejar
        // el fotograma de portada visible mientras se hace scroll
        { threshold: 0.01, rootMargin: '25% 0px 25% 0px' }
      );
      videos.forEach((video) => {
        video.muted = true;
        video.setAttribute('playsinline', '');
        io.observe(video);
      });
    }
  }

  /* --- 3 · Hitos de la cápsula iluminados según el vídeo --- */
  const capsulaWrap = document.querySelector('[data-vento-capsula-video]');
  const capsulaSection = capsulaWrap?.closest('.vento-capsula');
  const capsulaVideo = capsulaWrap?.querySelector('video');

  if (capsulaVideo && capsulaSection) {
    const tramos = Array.from(capsulaSection.querySelectorAll('[data-start]'))
      .map((el) => ({
        el,
        start: parseFloat(el.dataset.start),
        end: parseFloat(el.dataset.end),
      }))
      .filter(({ start, end }) => Number.isFinite(start) && Number.isFinite(end) && end > start);

    if (tramos.length) {
      // Solo atenuamos si de verdad hay tramos que iluminar
      capsulaSection.classList.add('vento-capsula--sync');

      let activo = null;
      const pintar = () => {
        const t = capsulaVideo.currentTime;
        const actual = tramos.find(({ start, end }) => t >= start && t < end) ?? null;
        if (actual === activo) return;
        activo?.el.classList.remove('is-active');
        actual?.el.classList.add('is-active');
        activo = actual;
      };

      capsulaVideo.addEventListener('timeupdate', pintar);
      capsulaVideo.addEventListener('seeked', pintar);
      capsulaVideo.addEventListener('loadedmetadata', pintar);
      pintar();
    }
  }

  /* --- 4 · Carrusel de vídeos de reseñas --- */
  document.querySelectorAll('[data-vento-carrusel]').forEach((carrusel) => {
    const track = carrusel.querySelector('[data-vento-carrusel-track]');
    if (!track) return;

    // Las reseñas de Judge.me llegan después de iniciar el carrusel: las
    // tarjetas se leen siempre en vivo y no exigimos un mínimo al arrancar.
    if (track.children.length < 2 && !track.hasAttribute('data-vento-resenas-live')) return;

    // Mismo carrusel en horizontal o vertical: solo cambia el eje
    const vertical = carrusel.dataset.eje === 'y';
    const medida = () => {
      if (!track.firstElementChild) return 0;
      const caja = track.firstElementChild.getBoundingClientRect();
      const estilos = getComputedStyle(track);
      const hueco = parseFloat(vertical ? estilos.rowGap : estilos.columnGap) || 0;
      return (vertical ? caja.height : caja.width) + hueco;
    };
    const posicion = () => (vertical ? track.scrollTop : track.scrollLeft);
    const finalAlcanzado = () =>
      vertical
        ? track.scrollTop >= track.scrollHeight - track.clientHeight - 2
        : track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;

    const mover = (dir) => {
      const inicio = vertical ? { top: 0 } : { left: 0 };
      const salto = vertical ? { top: medida() * dir } : { left: medida() * dir };
      if (dir > 0 && finalAlcanzado()) track.scrollTo({ ...inicio, behavior: 'smooth' });
      else track.scrollBy({ ...salto, behavior: 'smooth' });
    };

    /* Arrastre con ratón. En táctil no lo tocamos: el scroll nativo ya
       funciona y secuestrarlo rompe el gesto vertical de la página. */
    let arrastrando = false;
    let inicioPuntero = 0;
    let inicioScroll = 0;

    track.addEventListener('pointerdown', (event) => {
      // Sobre un vídeo o un control, no arrastramos: la captura del puntero
      // se comería el clic y el botón de sonido no llegaría a dispararse.
      if (event.pointerType !== 'mouse' || event.target.closest('video, button, a')) return;
      arrastrando = true;
      inicioPuntero = vertical ? event.clientY : event.clientX;
      inicioScroll = posicion();
      track.classList.add('is-dragging');
      track.setPointerCapture(event.pointerId);
    });

    track.addEventListener('pointermove', (event) => {
      if (!arrastrando) return;
      event.preventDefault();
      const avance = inicioScroll - ((vertical ? event.clientY : event.clientX) - inicioPuntero);
      if (vertical) track.scrollTop = avance;
      else track.scrollLeft = avance;
    });

    const soltar = () => {
      if (!arrastrando) return;
      arrastrando = false;
      track.classList.remove('is-dragging');
    };
    track.addEventListener('pointerup', soltar);
    track.addEventListener('pointercancel', soltar);

    /* Avance automático: se detiene con el ratón encima, mientras se
       arrastra y mientras cualquier vídeo esté reproduciéndose. */
    const segundos = parseFloat(carrusel.dataset.autoplay);
    if (!Number.isFinite(segundos) || segundos <= 0 || reduceMotion) return;

    let enPausa = false;
    const videos = Array.from(track.querySelectorAll('video'));
    // Solo frena el carrusel un vídeo con sonido: los que arrancan solos
    // van silenciados y no deben impedir que gire.
    const hayVideoSonando = () => videos.some((v) => !v.paused && !v.ended && !v.muted);

    carrusel.addEventListener('pointerenter', () => { enPausa = true; });
    carrusel.addEventListener('pointerleave', () => { enPausa = false; });
    carrusel.addEventListener('focusin', () => { enPausa = true; });
    carrusel.addEventListener('focusout', () => { enPausa = false; });

    // Si todo cabe a la vista no hay nada que girar
    const desborda = () =>
      vertical
        ? track.scrollHeight > track.clientHeight + 2
        : track.scrollWidth > track.clientWidth + 2;

    setInterval(() => {
      if (enPausa || arrastrando || hayVideoSonando() || document.hidden || !desborda()) return;
      mover(1);
    }, segundos * 1000);
  });

  /* --- 5 · Sonido de los vídeos de reseñas --- */
  const botonesSonido = Array.from(document.querySelectorAll('[data-vento-sonido]'));

  botonesSonido.forEach((boton) => {
    const contenedor = boton.closest('.vento-resenas__video');
    const video = contenedor?.querySelector('video');
    if (!video) return;

    // Sin menú contextual de descarga ni imagen sobre imagen
    video.setAttribute('controlslist', 'nodownload noplaybackrate');
    video.disablePictureInPicture = true;

    const pintar = () => {
      boton.setAttribute('aria-pressed', String(!video.muted));
      boton.setAttribute('aria-label', video.muted ? 'Activar sonido' : 'Silenciar');
    };

    boton.addEventListener('click', () => {
      const activar = video.muted;

      // Solo un vídeo con sonido a la vez
      if (activar) {
        botonesSonido.forEach((otro) => {
          const otroVideo = otro.closest('.vento-resenas__video')?.querySelector('video');
          if (otroVideo && otroVideo !== video) {
            otroVideo.muted = true;
            otro.setAttribute('aria-pressed', 'false');
            otro.setAttribute('aria-label', 'Activar sonido');
          }
        });
      }

      video.muted = !activar;
      if (activar) video.play().catch(() => {});
      pintar();
    });

    video.addEventListener('volumechange', pintar);
    pintar();
  });

  /* --- 6 · Visor ampliado de la galería de compra --- */
  const zoomDialog = document.querySelector('[data-vento-zoom-dialog]');
  const zoomTriggers = Array.from(document.querySelectorAll('[data-vento-zoom]'));

  if (zoomDialog && zoomTriggers.length) {
    const zoomImg = zoomDialog.querySelector('[data-vento-zoom-img]');
    let current = 0;

    const show = (index) => {
      current = (index + zoomTriggers.length) % zoomTriggers.length;
      const trigger = zoomTriggers[current];
      zoomImg.src = trigger.dataset.zoomSrc;
      zoomImg.alt = trigger.dataset.zoomAlt || '';
    };

    zoomTriggers.forEach((trigger, index) => {
      trigger.addEventListener('click', () => {
        show(index);
        zoomDialog.showModal();
      });
    });

    zoomDialog.querySelector('[data-vento-zoom-close]')?.addEventListener('click', () => zoomDialog.close());
    zoomDialog.querySelector('[data-vento-zoom-prev]')?.addEventListener('click', () => show(current - 1));
    zoomDialog.querySelector('[data-vento-zoom-next]')?.addEventListener('click', () => show(current + 1));

    // Cerrar al pulsar la propia imagen o el fondo
    zoomImg?.addEventListener('click', () => zoomDialog.close());
    zoomDialog.addEventListener('click', (event) => {
      if (event.target === zoomDialog) zoomDialog.close();
    });

    document.addEventListener('keydown', (event) => {
      if (!zoomDialog.open) return;
      if (event.key === 'ArrowRight') show(current + 1);
      if (event.key === 'ArrowLeft') show(current - 1);
    });

    // Si solo hay una imagen, las flechas sobran
    if (zoomTriggers.length < 2) {
      zoomDialog.querySelectorAll('.vento-zoom__nav').forEach((nav) => nav.remove());
    }
  }

  /* --- 6b · «Escribir una reseña»: abre el formulario de Judge.me --- */
  // Widget nuevo de Judge.me: su botón abre una ventana modal colgada del body.
  // Se mantiene el selector del widget antiguo por si la tienda vuelve a él.
  const abrirFormularioResena = (app) => {
    const botonJudgeMe = app.querySelector('.jm-action-buttons__button, .jdgm-write-rev-link');
    if (!botonJudgeMe) {
      // Judge.me aún no ha montado su widget: al menos llevamos a las reseñas
      (app.closest('.vento-resenas') || app).scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'start',
      });
      return false;
    }
    const modal = document.querySelector('.jdgm-write-review-modal');
    const form = app.querySelector('.jdgm-form-wrapper');
    const abierto = (modal && modal.offsetHeight > 0) || (form && form.offsetHeight > 0);
    if (!abierto) botonJudgeMe.click();
    return true;
  };

  document.querySelectorAll('[data-vento-escribir-resena]').forEach((boton) => {
    boton.addEventListener('click', () => {
      // El de la sección de compra no está dentro de las reseñas: buscamos en la página
      const app =
        boton.closest('.vento-resenas')?.querySelector('[data-vento-resenas-app]') ||
        document.querySelector('[data-vento-resenas-app]');
      if (app) abrirFormularioResena(app);
    });
  });

  /* --- 6c · Reseñas aprobadas en Judge.me, pintadas en nuestras tarjetas ---
     Usamos el mismo endpoint público que su widget: solo devuelve reseñas
     publicadas. El texto se inserta siempre como texto, nunca como HTML. */
  const listaEnVivo = document.querySelector('[data-vento-resenas-live]');
  if (listaEnVivo) {
    const { shop, productId } = listaEnVivo.dataset;
    const params = new URLSearchParams({
      url: shop,
      shop_domain: shop,
      platform: 'shopify',
      product_id: productId,
      page: '1',
      per_page: '30',
    });

    const textoPlano = (html) =>
      (new DOMParser().parseFromString(html || '', 'text/html').body.textContent || '').trim();

    const crearTarjeta = (resena) => {
      const nota = Math.max(0, Math.min(5, Math.round(Number(resena.rating) || 0)));
      const tarjeta = document.createElement('article');
      tarjeta.className = 'vento-resenas__card';

      const estrellas = document.createElement('div');
      estrellas.className = 'vento-resenas__stars';
      estrellas.setAttribute('role', 'img');
      estrellas.setAttribute('aria-label', `Valoración: ${nota} de 5`);
      for (let i = 1; i <= 5; i += 1) {
        const estrella = document.createElement('span');
        estrella.textContent = '★';
        estrella.setAttribute('aria-hidden', 'true');
        if (i <= nota) estrella.className = 'is-on';
        estrellas.append(estrella);
      }

      const texto = document.createElement('p');
      texto.className = 'vento-resenas__text';
      const titulo = (resena.title || '').trim();
      const cuerpo = textoPlano(resena.body_html);
      texto.textContent = titulo && !cuerpo.startsWith(titulo) ? `${titulo}. ${cuerpo}` : cuerpo;

      const autor = document.createElement('p');
      autor.className = 'vento-resenas__author';
      autor.textContent = resena.reviewer_name || 'Cliente';
      if (resena.verified_buyer === 'buyer') {
        const sello = document.createElement('span');
        sello.className = 'vento-resenas__verificado';
        sello.setAttribute('role', 'img');
        sello.setAttribute('aria-label', 'Compra verificada');
        sello.title = 'Compra verificada';
        sello.innerHTML =
          '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="currentColor"></circle><path d="m8.4 12.3 2.4 2.4 4.8-5.2" stroke="#0b0b0c" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"></path></svg>';
        autor.append(sello);
      }

      tarjeta.append(estrellas, texto, autor);
      return tarjeta;
    };

    fetch(`https://api.judge.me/reviews/reviews_for_widget?${params}`, {
      headers: { Accept: 'application/json' },
    })
      .then((respuesta) => (respuesta.ok ? respuesta.json() : null))
      .then((datos) => {
        const resenas = (datos && Array.isArray(datos.reviews) ? datos.reviews : []).filter(
          (resena) => textoPlano(resena.body_html)
        );
        if (!resenas.length) return;
        // Las reales van delante de las escritas a mano en el editor
        listaEnVivo.prepend(...resenas.map(crearTarjeta));
      })
      .catch(() => {});
  }

  // Desde la portada se llega a la ficha con #escribir-resena: abrimos el form
  // en cuanto Judge.me haya montado su widget (carga tras el resto de la página)
  const appResenas = document.querySelector('[data-vento-resenas-app]');
  if (appResenas && window.location.hash === '#escribir-resena') {
    const intentar = (restantes) => {
      if (abrirFormularioResena(appResenas) || restantes <= 0) return;
      setTimeout(() => intentar(restantes - 1), 300);
    };
    const empezar = () => setTimeout(() => intentar(20), 300);
    if (document.readyState === 'complete') empezar();
    else window.addEventListener('load', empezar, { once: true });
  }

  /* --- 7 · Aparición al hacer scroll --- */
  const els = document.querySelectorAll('[data-vento-reveal]');
  if (!els.length || reduceMotion || !('IntersectionObserver' in window)) return;

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('vento-in');
          io.unobserve(entry.target);
        }
      }
    },
    { threshold: 0.15, rootMargin: '0px 0px -48px 0px' }
  );

  els.forEach((el) => {
    // Solo ocultamos si el elemento está fuera del viewport inicial,
    // para no penalizar LCP ni provocar parpadeos.
    const rect = el.getBoundingClientRect();
    if (rect.top > window.innerHeight * 0.85) {
      el.classList.add('vento-pre');
      io.observe(el);
    }
  });
})();
